import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@core-ui/Components/ui/card';
import { Button } from '@core-ui/Components/ui/button';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '@ui/shared/searchInput';
import { PaginationControls } from '@ui/shared/paginationControls';
import { usePaginationData } from '@hooks/usePaginationData';

import { useSessionStore } from '@features/auth/store/useSessionStore';
import useVoicemailApi, { type VoicemailDto } from '@features/voicemail/hooks/useVoicemailApi';

import ConfirmDeleteVoicemailDialog from '@features/voicemail/components/ConfirmDeleteVoicemailDialog';
import RenameVoicemailDialog from '@features/voicemail/components/RenameVoicemailDialog';
import CreateVoicemailDialog from '@features/voicemail/components/CreateVoicemailDialog';
import VoicemailTable from '@features/voicemail/components/VoicemailTable';

import WaveMini from '@features/voicemail/components/WaveMini';
import PlayIcon from '@features/voicemail/components/PlayIcon';

import { formatDate } from '@utils/date.ts';
import { formatDuration } from '@utils/formatDuration';
import { toDurationSec } from '@utils/toDurationSec';
import { type VoicemailApiCompat } from '../hooks/useVoicemailApi';

import { useTwilioVoice } from '@hooks/useTwilioVoice';
import MicrophoneSetupModal from '@ui/shared/Dialer/MicrophoneSetupModal.tsx';
import { paintToast } from '@lib/logger/paintToast.ts';
import { LogLevels } from '@types';
import { writeInfoLog } from '@lib/logger/logInfo.ts';
import { writeSuccessLog } from '@lib/logger/logSuccess.tsx';
import { writeErrorLog } from '@lib/logger/logError.ts';
import { writeWarningLog } from '@lib/logger/logWarning.ts';

type VMStatus = 'active' | 'draft' | 'default';

export interface VoicemailRow {
  id: string;
  name: string;
  durationSec: number;
  status: VMStatus;
  updatedAt: string;
  audioUrl: string;
}

function useDebounce<T> (value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, delay);

    return () => {
      window.clearTimeout(id);
    };
  }, [value, delay]);

  return debounced;
}

export default function VoicemailsDashboard (): React.JSX.Element {
  const { t } = useTranslation();

  const currentUser = useSessionStore((s) => s.currentUser);
  const userId = (currentUser?.userId ?? '').trim();

  const { getVoicemailsBySdrIdAsync, deleteVoicemailAsync, updateVoicemailAsync } = useVoicemailApi();

  // Twilio (Create voicemail)
  const {
    status: twilioStatus,
    error: twilioError,
    startCall,
    sendDigits,
    currentCall,
  } = useTwilioVoice(userId !== '' ? userId : undefined);

  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<VoicemailRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 400);

  // audio playback
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  // dialogs
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [vmToDelete, setVmToDelete] = useState<VoicemailRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [renameOpen, setRenameOpen] = useState(false);
  const [vmToRename, setVmToRename] = useState<VoicemailRow | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  // create
  const [createOpen, setCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  // search ref
  const latestSearchRef = useRef<string>('');
  useEffect(() => {
    latestSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const durationCacheRef = useRef<Record<string, number>>({});

  const mapApiToRow = useCallback((vm: VoicemailDto): VoicemailRow => {
    const v = vm as VoicemailApiCompat;

    const compatId = String(v.id ?? v._id ?? '').trim();
    const audioUrl = String(v.recordingUrl ?? v.url ?? '').trim();

    const rawDuration =
      v.durationSec ??
      v.duration ??
      v.recordingDuration ??
      v.duration_seconds ??
      v.length ??
      0;

    const apiDurationSec = toDurationSec(rawDuration);
    const cached = compatId !== '' ? durationCacheRef.current[compatId] : undefined;

    const durationSec = apiDurationSec > 0 ? apiDurationSec : (cached ?? 0);

    const updatedAt = String(v.updatedAt ?? v.createdAt ?? '');
    const status: VMStatus = (v.status ?? 'active');

    return {
      id: compatId,
      name: String(v.title ?? ''),
      durationSec,
      status,
      updatedAt,
      audioUrl,
    };
  }, []);

  const fetchVoicemails = useCallback(
    async (page: number, limit: number, q: string): Promise<void> => {
      if (userId === '') {
        setRows([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      setLoading(true);
      await writeInfoLog( t('voice.voicemails.toast.infoLoading', { lng: 'en' }), 'VoicemailsDashboard.fetchVoicemails', { userId, page, limit, q });

      try {
        const res = await getVoicemailsBySdrIdAsync(userId);
        if (!res.success) {
          throw new Error(res.error?.message ?? 'Error fetching voicemails');
        }

        const all = (res.data ?? []).map(mapApiToRow).filter((x) => x.id !== '');

        const qLower = q.trim().toLowerCase();
        const filtered = all.filter((vm) => (qLower === '' ? true : vm.name.toLowerCase().includes(qLower)));

        setTotalCount(filtered.length);

        const start = (page - 1) * limit;
        const paged = filtered.slice(start, start + limit);

        setRows(paged);

        // fill durations by metadata (only missing)
        void (async (): Promise<void> => {
          const candidates = paged.filter((x) => x.durationSec === 0 && x.audioUrl !== '' && x.id !== '');

          await Promise.all(
            candidates.map(async (vm): Promise<void> => {
              const cachedDuration = durationCacheRef.current[vm.id];
              if (cachedDuration !== undefined && cachedDuration > 0) {
                return;
              }

              try {
                const a = new Audio();
                a.preload = 'metadata';
                a.src = vm.audioUrl;

                await new Promise<void>((resolve, reject): void => {
                  const onLoaded = (): void => {
                    resolve();
                  };
                  const onErr = (): void => {
                    reject(new Error('metadata_error'));
                  };
                  a.addEventListener('loadedmetadata', onLoaded, { once: true });
                  a.addEventListener('error', onErr, { once: true });
                });

                const d = Number.isFinite(a.duration) ? Math.floor(a.duration) : 0;
                if (d > 0) {
                  durationCacheRef.current[vm.id] = d;
                  setRows((prev) => prev.map((r) => (r.id === vm.id ? { ...r, durationSec: d } : r)));
                }
              } catch {}
            }),
          );
        })();

        await writeSuccessLog(
          t('voice.voicemails.toast.successLoaded', { lng: 'en' }),
          'VoicemailsDashboard.fetchVoicemails',
          { count: paged.length, total: filtered.length },
        );
      } catch (e) {
        await writeErrorLog(
          t('voice.voicemails.toast.errorFetch', { lng: 'en' }),
          'VoicemailsDashboard.fetchVoicemails',
          { userId, page, limit, q, error: String((e as Error)?.message ?? e) }
        );
        paintToast(LogLevels.ERROR, t('voice.voicemails.toast.errorFetch'));
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [getVoicemailsBySdrIdAsync, mapApiToRow, userId],
  );

  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } = usePaginationData<VoicemailRow>({
    mode: 'server',
    totalItems: totalCount,
    onServerRequest: useCallback(
      (page: number, limit: number) => {
        void fetchVoicemails(page, limit, latestSearchRef.current);
      },
      [fetchVoicemails],
    ),
    initialRowsPerPage: 10,
    initialPage: 1,
  });

  useEffect(() => {
    setCurrentPage(1);
    void fetchVoicemails(1, rowsPerPage, latestSearchRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, rowsPerPage, userId]);

  useEffect(() => {
    const el = (audioRef.current ??= new Audio());

    const onTime = (): void => {
      const duration = el.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        setProgress(0);
        return;
      }
      setProgress(el.currentTime / duration);
    };

    const onEnd = (): void => {
      setPlayingId(null);
      setProgress(0);
    };

    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);

    return () => {
      el.pause();
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnd);
    };
  }, []);

  const togglePlay = useCallback(
    async (vm: VoicemailRow): Promise<void> => {
      if (vm.audioUrl === '') {
        await writeWarningLog( t('voice.voicemails.toast.warnNoAudio', { lng: 'en' }), 'VoicemailsDashboard.togglePlay', { id: vm.id });
        return;
      }

      const el = (audioRef.current ??= new Audio());

      if (playingId === vm.id) {
        if (!el.paused) {
          el.pause();
        } else {
          await el.play().catch(() => {});
        }
        return;
      }

      setPlayingId(vm.id);
      setProgress(0);

      el.src = vm.audioUrl;
      el.currentTime = 0;

      await el.play().catch(async (e) => {
        await writeWarningLog( t('voice.voicemails.toast.warnNoPlay', { lng: 'en' }), 'VoicemailsDashboard.togglePlay', { id: vm.id, error: String(e) });
        paintToast(LogLevels.WARNING, t('voice.voicemails.toast.warnNoPlay'));
      });
    },
    [playingId],
  );

  // delete
  const askDelete = useCallback(async (vm: VoicemailRow): Promise<void> => {
    setVmToDelete(vm);
    setDeleteOpen(true);
    await writeInfoLog( t('voice.voicemails.toast.infoDeleteModal', {  lng: 'en' }), 'VoicemailsDashboard.askDelete', { id: vm.id });
  }, []);

  const confirmDelete = useCallback(async (): Promise<void> => {
    if (vmToDelete?.id == null) {
      return;
    }

    setIsDeleting(true);
    await writeInfoLog( t('voice.voicemails.toast.infoDeleting', { lng: 'en' }), 'VoicemailsDashboard.confirmDelete', { id: vmToDelete.id });

    try {
      const res = await deleteVoicemailAsync(vmToDelete.id);
      if (!res.success) {
        throw new Error(res.error?.message ?? 'Delete failed');
      }

      if (playingId === vmToDelete.id) {
        setPlayingId(null);
        setProgress(0);
        audioRef.current?.pause();
      }

      await writeSuccessLog( t('voice.voicemails.toast.successDelete', { lng: 'en' }), 'VoicemailsDashboard.confirmDelete', { id: vmToDelete.id });
      paintToast(LogLevels.SUCCESS, t('voice.voicemails.toast.successDelete'));

      setDeleteOpen(false);
      setVmToDelete(null);

      void fetchVoicemails(currentPage, rowsPerPage, latestSearchRef.current);
    } catch (e) {
      await writeErrorLog(
        t('voice.voicemails.toast.errorDelete', { lng: 'en' }),
        'VoicemailsDashboard.confirmDelete',
        { id: vmToDelete.id, error: String((e as Error)?.message ?? e) }
      );
      paintToast(LogLevels.ERROR, t('voice.voicemails.toast.errorDelete'));
    } finally {
      setIsDeleting(false);
    }
  }, [currentPage, deleteVoicemailAsync, fetchVoicemails, playingId, rowsPerPage, vmToDelete]);

  // rename
  const openRename = useCallback(async (vm: VoicemailRow): Promise<void> => {
    setVmToRename(vm);
    setRenameOpen(true);
    await writeInfoLog( t('voice.voicemails.toast.infoRenameModal', { lng: 'en' }), 'VoicemailsDashboard.openRename', { id: vm.id });
  }, []);

  const saveRename = useCallback(
    async (title: string): Promise<void> => {
      if (vmToRename?.id == null) {
        return;
      }

      const clean = title.trim();
      if (clean === '') {
        await writeWarningLog( t('voice.voicemails.toast.warnTitle', { lng: 'en' }), 'VoicemailsDashboard.saveRename.validation', { id: vmToRename.id });
        paintToast(LogLevels.WARNING, t('voice.voicemails.toast.warnTitle'));
        return;
      }

      setIsRenaming(true);
      await writeInfoLog( t('voice.voicemails.toast.infoRenaming', { lng:'en' }), 'VoicemailsDashboard.saveRename', { id: vmToRename.id, title: clean });

      try {
        const res = await updateVoicemailAsync({ id: vmToRename.id, title: clean });
        if (!res.success) {
          throw new Error(res.error?.message ?? 'Rename failed');
        }

        await writeSuccessLog( t('voice.voicemails.toast.successRename', { lng: 'en' }), 'VoicemailsDashboard.saveRename', { id: vmToRename.id });
        paintToast(LogLevels.SUCCESS, t('voice.voicemails.toast.successRename'));

        setRenameOpen(false);
        setVmToRename(null);

        void fetchVoicemails(currentPage, rowsPerPage, latestSearchRef.current);
      } catch (e) {
        await writeErrorLog(
          t('voice.voicemails.toast.errorName', { lng: 'en' }),
          'VoicemailsDashboard.saveRename',
          { id: vmToRename.id, error: String((e as Error)?.message ?? e) }
        );
        paintToast(LogLevels.ERROR, t('voice.voicemails.toast.errorName'));
      } finally {
        setIsRenaming(false);
      }
    },
    [currentPage, fetchVoicemails, rowsPerPage, updateVoicemailAsync, vmToRename],
  );

  // create dialog open
  const openCreate = useCallback(async (): Promise<void> => {
    setCreateTitle('');
    setIsCreating(false);
    setIsRecording(false);
    setCreateOpen(true);

    await writeInfoLog( t('voice.voicemails.toast.infoCreateModal', { lng: 'en' }), 'VoicemailsDashboard.openCreate', { userId });

    if (twilioError != null && twilioError.trim() !== '') {
      await writeWarningLog( t('voice.voicemails.toast.warnTwilio', { lng: 'en' }), 'VoicemailsDashboard.openCreate.twilio', { twilioError });
      paintToast(LogLevels.WARNING, t('voice.voicemails.toast.warnTwilio'));
    }
  }, [twilioError, userId]);

  // start/stop recording
  const startRecordingVm = useCallback(async (): Promise<void> => {
    const clean = createTitle.trim();
    if (clean === '') {
      return;
    }

    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.error('[VM] Mic denied', e);
      return;
    } finally {
      stream?.getTracks().forEach((t) => {
        t.stop();
      });
    }

    await startCall(userId, {
      isVoicedrop: 'true',
      agent: userId,
      recordTitle: clean,
      newRecording: 'true',
      recordId: '',
    });

    const call = currentCall();
    if (call != null) {
      call.on('disconnect', () => {
        setIsRecording(false);
        setCreateOpen(false);
        setTimeout(() => {
          void fetchVoicemails(currentPage, rowsPerPage, latestSearchRef.current);
        }, 700);
      });
    }

    setIsRecording(true);
  }, [createTitle, startCall, currentCall]);

  // eslint-disable-next-line @typescript-eslint/require-await
  const stopRecordingVm = useCallback(async (): Promise<void> => {
    if (!isRecording) {
      return;
    }

    sendDigits('*');
    setIsRecording(false);
  }, [isRecording, sendDigits]);

  return (
    <div className="relative">
      <MicrophoneSetupModal />
      <Card className="p-6 rounded-2xl shadow-lg border border-border bg-card text-foreground">
        <CardHeader className="px-0">
          <CardTitle data-qa-id="voicemail-title" className="text-2xl font-bold text-foreground">
            {t('voice.voicemails.title', 'Voicemails')}
          </CardTitle>
          <CardDescription className="mt-1">
            {t('voice.voicemails.description')}
          </CardDescription>
        </CardHeader>

        <CardContent className="px-0">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
            <SearchInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              onClear={() => {
                setSearch('');
                setCurrentPage(1);
                void fetchVoicemails(1, rowsPerPage, '');
                void writeInfoLog( t('voice.voicemails.toast.infoClearSearch', { lng:'en' }), 'VoicemailsDashboard.search.clear', {});
              }}
              placeholder={t('voice.voicemails.searchPh', 'Search voicemail...')}
            />

            <Button
              data-qa-id="create-vm-button"
              type="button"
              className="bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
              onClick={() => {
                void openCreate();
              }}
              disabled={loading || userId === ''}
            >
              <CirclePlus className="mr-1 h-4 w-4" />
              {t('voice.voicemails.create.title', 'Create Voicemail')}
            </Button>
          </div>

          <VoicemailTable
            loading={loading}
            rows={rows}
            onPlay={togglePlay}
            isPlaying={(id) => id === playingId && (audioRef.current?.paused === false)}
            progress={progress}
            fmtDuration={formatDuration}
            fmtDate={formatDate}
            onEdit={openRename}
            onAskDelete={askDelete}
            skeletonRows={rowsPerPage}
            WaveMini={WaveMini}
            PlayIcon={PlayIcon}
          />

          <PaginationControls
            mode="server"
            totalItems={totalCount}
            currentPage={currentPage}
            onPageChange={(p) => {
              setCurrentPage(p);
              void fetchVoicemails(p, rowsPerPage, latestSearchRef.current);
            }}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(n) => {
              setRowsPerPage(n);
              setCurrentPage(1);
              void fetchVoicemails(1, n, latestSearchRef.current);
            }}
            isLoading={loading}
            className="mt-4"
          />
        </CardContent>

        <ConfirmDeleteVoicemailDialog
          open={deleteOpen}
          title={vmToDelete?.name ?? ''}
          loading={isDeleting}
          onCancel={() => {
            setDeleteOpen(false);
            setVmToDelete(null);
            void writeInfoLog( t('voice.voicemails.toast.infoDeleteCancel', { lng: 'en' }), 'VoicemailsDashboard.delete.cancel', {});
          }}
          onConfirm={confirmDelete}
        />

        <RenameVoicemailDialog
          open={renameOpen}
          initialTitle={vmToRename?.name ?? ''}
          audioUrl={vmToRename?.audioUrl ?? ''}
          loading={isRenaming}
          onCancel={() => {
            setRenameOpen(false);
            setVmToRename(null);
            void writeInfoLog( t('voice.voicemails.toast.infoRenameCancel', { lng:'en' }), 'VoicemailsDashboard.rename.cancel', {});
          }}
          onSave={saveRename}
        />

        <CreateVoicemailDialog
          open={createOpen}
          loading={isCreating}
          title={createTitle}
          recording={isRecording}
          twilioStatus={twilioStatus}
          onTitleChange={setCreateTitle}
          onCancel={() => {
            setCreateOpen(false);
            setIsCreating(false);
            setIsRecording(false);
            void writeInfoLog( t('voice.voicemails.toast.infoCreateCancel', { lng: 'en' }), 'VoicemailsDashboard.create.cancel', {});
          }}
          onStartRecording={() => {
            void startRecordingVm();
          }}
          onStopRecording={() => {
            void stopRecordingVm();
          }}
        />
      </Card>
    </div>
  );
}
